import { Fragment } from "react";
import { motion } from "framer-motion";
import { Check, Crown, Minus } from "lucide-react";
import { effectivePrice, isOnSale } from "@/lib/pricing";

const featureKey = (line) => {
  const l = line.toLowerCase();
  if (l.includes("gem")) return "Gem earnings";
  if (l.includes("auction")) return "Auction House slots";
  if (l.includes("order slot")) return "Order slots";
  if (l.includes("key")) return "Free crate key";
  return line;
};

const bulletsOf = (pkg) =>
  (pkg.description || "").split("\n").map((s) => s.trim()).filter(Boolean).slice(1);

const GemCell = ({ bullet }) => {
  const mult = bullet.match(/[\d.]+x/i)?.[0] || "";
  const afk = /afk/i.test(bullet);
  return (
    <span className="flex flex-col items-center gap-1.5">
      <span className="inline-flex items-center gap-2 text-slate-200">
        <Check className="w-4 h-4 shrink-0 text-[#8B5CF6]" strokeWidth={3} />
        <span className="font-semibold">{mult} per minute</span>
      </span>
      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide border ${
        afk
          ? "bg-amber-400/10 text-amber-300 border-amber-400/40"
          : "bg-emerald-400/10 text-emerald-300 border-emerald-400/40"
      }`}>
        {afk ? "ONLY IN /AFK" : "ANYWHERE, WHILE ONLINE"}
      </span>
    </span>
  );
};

export const RankCompare = ({ ranks, onPurchase }) => {
  const sorted = [...ranks].sort((a, b) => a.price - b.price);
  const perkMaps = sorted.map((pkg) => {
    const map = {};
    bulletsOf(pkg).forEach((b) => { map[featureKey(b)] = b; });
    return map;
  });
  const rows = [];
  perkMaps.forEach((map) => {
    Object.keys(map).forEach((key) => { if (!rows.includes(key)) rows.push(key); });
  });
  if (rows.length === 0) return null;
  const best = sorted[sorted.length - 1];

  return (
    <motion.section
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="mt-24"
      data-testid="rank-compare"
    >
      <p className="text-[11px] font-semibold tracking-[0.35em] text-[#A78BFA] text-center">SIDE BY SIDE</p>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight text-white text-center">
        Compare the <span className="italic text-[#A78BFA]">ranks</span>
      </h2>

      <div className="mt-10 overflow-x-auto">
        <div className="min-w-[640px] rounded-2xl border border-white/10 bg-[#0D0B12]/80 overflow-hidden">
          <div className="grid" style={{ gridTemplateColumns: `1.4fr repeat(${sorted.length}, 1fr)` }}>
            <div className="p-5 border-b border-white/10" />
            {sorted.map((pkg) => (
              <div
                key={pkg.id}
                className={`p-5 border-b border-l border-white/10 text-center ${pkg.id === best.id ? "bg-[#8B5CF6]/10" : ""}`}
              >
                {pkg.id === best.id && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] text-amber-300">
                    <Crown className="w-3 h-3" /> BEST VALUE
                  </span>
                )}
                <p className="font-display text-2xl text-white mt-1">{pkg.name}</p>
                <p className="mt-1 font-mono text-[#A78BFA]">
                  {isOnSale(pkg) && <span className="line-through text-slate-500 text-xs mr-2">${pkg.price.toFixed(2)}</span>}
                  ${effectivePrice(pkg).toFixed(2)}{pkg.recurring && <span className="text-xs text-slate-500">/{pkg.recurring === "month" ? "mo" : pkg.recurring}</span>}
                </p>
              </div>
            ))}

            {rows.map((row, i) => (
              <Fragment key={row}>
                <div className={`p-5 text-sm text-slate-400 ${i % 2 ? "bg-white/[0.02]" : ""} ${i === rows.length - 1 ? "" : "border-b border-white/5"}`} data-testid={`rank-compare-row-${i}`}>
                  {row}
                </div>
                {sorted.map((pkg, j) => {
                  const value = perkMaps[j][row];
                  return (
                    <div
                      key={`${pkg.id}-${row}`}
                      className={`p-5 border-l border-white/10 text-center text-sm ${i % 2 ? "bg-white/[0.02]" : ""} ${i === rows.length - 1 ? "" : "border-b border-white/5"} ${pkg.id === best.id ? "bg-[#8B5CF6]/10" : ""}`}
                    >
                      {value ? (
                        row === "Gem earnings" ? (
                          <GemCell bullet={value} />
                        ) : (
                          <span className="inline-flex items-center gap-2 text-slate-200">
                            <Check className="w-4 h-4 shrink-0 text-[#8B5CF6]" strokeWidth={3} />
                            {value}
                          </span>
                        )
                      ) : (
                        <Minus className="w-4 h-4 text-slate-700 mx-auto" />
                      )}
                    </div>
                  );
                })}
              </Fragment>
            ))}

            <div className="p-5 border-t border-white/10" />
            {sorted.map((pkg) => (
              <div key={`buy-${pkg.id}`} className={`p-5 border-t border-l border-white/10 ${pkg.id === best.id ? "bg-[#8B5CF6]/10" : ""}`}>
                <button
                  onClick={() => onPurchase(pkg)}
                  data-testid={`rank-compare-buy-${pkg.id}`}
                  className={`w-full py-3 rounded-xl text-xs font-bold tracking-[0.2em] transition-[background-color,color,border-color] duration-300 ${
                    pkg.id === best.id
                      ? "bg-[#8B5CF6] text-white hover:bg-[#7C3AED] shadow-[0_0_24px_rgba(139,92,246,0.4)]"
                      : "bg-[#8B5CF6]/15 border border-[#8B5CF6]/40 text-[#C4B5FD] hover:bg-[#8B5CF6] hover:text-white"
                  }`}
                >
                  PURCHASE
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
};
